import React from 'react';
import classNames from 'classnames';
import style from './collapsable.module.scss';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';

interface CollapsableSetProps {
    children: Array<React.ReactElement<React.SFC<TitleProps>> | null>;
    initiallyExpanded?: boolean;
    allSizes?: boolean;
    ExpandAll: React.ComponentType<ExpandAllProps>;
}

interface CollapsableSetState {
    // A rendered Collapsable section with at least one element can be either expanded, or collapsed.
    // This is described using a boolean flag.
    // However, if notion of collapsing is invalid for a section in some way (e.g. it has no elements),
    // then we describe that with undefined value.
    collapsed: Array<boolean | undefined>;
}

export class CollapsableSet extends React.Component<CollapsableSetProps, CollapsableSetState> {
    constructor(props: CollapsableSetProps) {
        super(props);
        this.state = { collapsed: [] };
    }

    componentWillMount(): void {
        this.setCollapsedAll(!this.props.initiallyExpanded);
    }

    private setCollapsed = (index: number, collapsed: boolean | undefined) => {
        const state = [...this.state.collapsed];
        state[index] = collapsed;
        this.setState({ collapsed: state });
    }

    private get allExpanded(): boolean {
        return this.state.collapsed.every(collapsed => collapsed !== true);
    }

    private setCollapsedAll = (collapsed: boolean) => {
        const newState = React.Children.map(
            this.props.children,
            child => child ? collapsed : undefined);

        this.setState({ collapsed: newState });
    }

    render(): JSX.Element {
        const ExpandAll = this.props.ExpandAll;
        // We only map over the non-null children as this.state.collapsed only has elements for those
        const children = React.Children.map(this.props.children, child => child ? child : undefined);
        return (
            <div>
                <ExpandAll
                    collapsed={!this.allExpanded}
                    setCollapsed={collapsed => this.setCollapsedAll(collapsed)}
                />
                <div>
                    {React.Children.map(children, (child, index) =>
                        React.cloneElement(child as React.ReactElement<any>, {
                            collapsed: this.state.collapsed[index],
                            allSizes: this.props.allSizes,
                            setCollapsed: (collapsed: boolean | undefined) => this.setCollapsed(index, collapsed)
                        })
                    )}
                </div>
            </div>
        );
    }
}

export interface ExpandAllProps {
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
}

export interface TitleProps {
    collapsed?: boolean | undefined;
    setCollapsed?: (collapsed: boolean | undefined) => void;
    allSizes?: boolean;
}

/**
 * Takes a component and prepares it to be part of a collapsable set.
 * @param title A title element that will handle onclick events using the
 *      callbacks provided to its props.
 * @param WrappedComponent Any component SFC that will be wrapped to show
 *      and hide on click of the title.
 */
export function withCollapsable<P>(
    title: React.ReactElement<TitleProps>,
    Content: React.SFC<Readonly<P>>
): React.ComponentType<P & TitleProps> {

    class Collapsable extends React.Component<P & TitleProps, {}> {

        componentDidMount(): void {
            this.updateOwnCollapsedState(this.props);
        }
        componentWillReceiveProps(nextProps: Readonly<P & TitleProps>): void {
            this.updateOwnCollapsedState(nextProps);
        }

        private updateOwnCollapsedState(props: Readonly<P & TitleProps>): void {
            if (props.setCollapsed) {
                // If we can't show any content, then the collapsable state of the current section can not be defined.
                if (props.collapsed !== undefined && !Content(props)) {
                    props.setCollapsed(undefined);
                }
            }
        }

        render(): JSX.Element | null {
            const result = Content(this.props);
            if (!result) {
                return null;
            }

            const injectedTitleProps: TitleProps = {
                collapsed: this.props.collapsed,
                setCollapsed: this.props.setCollapsed || (c => {})
            };
            const titleWithProps = React.cloneElement(title, injectedTitleProps);

            return (
                <div
                    className={classNames({
                        [style.Collapsable]: !this.props.allSizes,
                        [style.CollapsableAlways]: this.props.allSizes
                    })}
                >
                    {titleWithProps}
                    <div
                        className={classNames({
                            [style.Hidden]: this.props.collapsed && !this.props.allSizes,
                            [style.HiddenAlways]: this.props.collapsed && this.props.allSizes
                            })}
                    >
                        {result}
                    </div>
                </div>
            );
        }
    }
    return Collapsable;
}
